import React from 'react';
import { motion } from 'motion/react';
import { ArrowDownRight, Globe, Award, Sparkles, Users, Linkedin, Instagram } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';

const images = import.meta.glob<{ default: string }>('../assets/images/*.{png,jpg,jpeg,svg,webp}', { eager: true });

const getImageUrl = (path: string) => {
  if (!path) return '';
  const filename = path.split('/').pop();
  return images[`../assets/images/${filename}`]?.default || path;
};

export default function Hero() {
  const [logoError, setLogoError] = React.useState(false);
  const isMobile = useIsMobile();

  const handleScrollToWork = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const element = document.getElementById('work');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const clientLogos = [
    {
      id: 'kyoto',
      name: 'Kyoto Forest Logo',
      src1: '/src/assets/images/regenerated_image_1780016814747.webp',
      src2: '/src/assets/images/regenerated_image_1780016814747.webp',
    },
    {
      id: 'nova',
      name: 'Nova Compute Logo',
      src1: '/src/assets/images/regenerated_image_1780016811407.webp',
      src2: '/src/assets/images/regenerated_image_1780016811407.webp',
    },
    {
      id: 'onyx',
      name: 'Onyx Summit Logo',
      src1: '/src/assets/images/regenerated_image_1780016809658.webp',
      src2: '/src/assets/images/regenerated_image_1780016809658.webp',
    },
    {
      id: 'verdant',
      name: 'Verdant Bio Logo',
      src1: '/src/assets/images/regenerated_image_1780016806394.webp',
      src2: '/src/assets/images/regenerated_image_1780016806394.webp',
    },
    {
      id: 'kinetic',
      name: 'Kinetic Labs Logo',
      src1: '/src/assets/images/regenerated_image_1780016804943.webp',
      src2: '/src/assets/images/regenerated_image_1780016804943.webp',
    },
    {
      id: 'pulse',
      name: 'Pulse Dynamic Logo',
      src1: '/src/assets/images/regenerated_image_1780016802253.webp',
      src2: '/src/assets/images/regenerated_image_1780016802253.webp',
    },
    {
      id: 'forest',
      name: 'Forest Group Logo',
      src1: '/src/assets/images/regenerated_image_1780016799957.webp',
      src2: '/src/assets/images/regenerated_image_1780016799957.webp',
    },
    {
      id: 'bio',
      name: 'Bio Corp Logo',
      src1: '/src/assets/images/regenerated_image_1780016798745.webp',
      src2: '/src/assets/images/regenerated_image_1780016798745.webp',
    },
    {
      id: 'cloud',
      name: 'Cloud Systems Logo',
      src1: '/src/assets/images/regenerated_image_1780016795588.webp',
      src2: '/src/assets/images/regenerated_image_1780016795588.webp',
    },
    {
      id: 'tech',
      name: 'Tech Solutions Logo',
      src1: '/src/assets/images/regenerated_image_1780016793995.webp',
      src2: '/src/assets/images/regenerated_image_1780016793995.webp',
    },
    {
      id: 'apex',
      name: 'Apex Studio Logo',
      src1: '/src/assets/images/regenerated_image_1780016792741.webp',
      src2: '/src/assets/images/regenerated_image_1780016792741.webp',
    },
    {
      id: 'vortex',
      name: 'Vortex Cloud Logo',
      src1: '/src/assets/images/regenerated_image_1780016789844.webp',
      src2: '/src/assets/images/regenerated_image_1780016789844.webp',
    },
    {
      id: 'lumina',
      name: 'Lumina Deep Logo',
      src1: '/src/assets/images/regenerated_image_1780016787730.webp',
      src2: '/src/assets/images/regenerated_image_1780016787730.webp',
    },
    {
      id: 'zenith',
      name: 'Zenith Space Logo',
      src1: '/src/assets/images/regenerated_image_1780016784404.webp',
      src2: '/src/assets/images/regenerated_image_1780016784404.webp',
    },
    {
      id: 'nebula',
      name: 'Nebula Labs Logo',
      src1: '/src/assets/images/regenerated_image_1780016782786.webp',
      src2: '/src/assets/images/regenerated_image_1780016782786.webp',
    },
    {
      id: 'horizon',
      name: 'Horizon Labs Logo',
      src1: '/src/assets/images/regenerated_image_1780016780888.webp',
      src2: '/src/assets/images/regenerated_image_1780016780888.webp',
    },
    {
      id: 'solis',
      name: 'Solis Energy Logo',
      src1: '/src/assets/images/regenerated_image_1780002839673.webp',
      src2: '/src/assets/images/regenerated_image_1780002839673.webp',
    },
    {
      id: 'stratus',
      name: 'Stratus Tech Logo',
      src1: '/src/assets/images/regenerated_image_1780005996071.webp',
      src2: '/src/assets/images/regenerated_image_1780005996071.webp',
    },
    {
      id: 'pulsar',
      name: 'Pulsar Systems Logo',
      src1: '/src/assets/images/regenerated_image_1780005997956.webp',
      src2: '/src/assets/images/regenerated_image_1780005997956.webp',
    },
    {
      id: 'organic',
      name: 'Organic Foods Logo',
      src1: '/src/assets/images/regenerated_image_1780005999143.webp',
      src2: '/src/assets/images/regenerated_image_1780005999143.webp',
    },
    {
      id: 'omega',
      name: 'Omega Holdings Logo',
      src1: '/src/assets/images/regenerated_image_1780006001610.webp',
      src2: '/src/assets/images/regenerated_image_1780006001610.webp',
    },
    {
      id: 'prime',
      name: 'Prime Solutions Logo',
      src1: '/src/assets/images/regenerated_image_1780006021352.webp',
      src2: '/src/assets/images/regenerated_image_1780006021352.webp',
    }
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen pt-32 pb-20 flex flex-col justify-between overflow-x-clip bg-transparent text-neutral-900 dark:text-white transition-colors duration-500"
    >
      {/* Background Ambience / Blur Overlays replicating premium high-end portfolio layout */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-950/20 via-neutral-950/20 to-neutral-950 pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -top-40 right-20 w-[450px] h-[450px] bg-emerald-500/5 blur-[130px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />
      <div className="absolute -bottom-40 left-10 w-[550px] h-[550px] bg-emerald-500/5 blur-[150px] rounded-full pointer-events-none opacity-0 dark:opacity-100 transition-opacity duration-500" />

      {/* Background ambient lighting effects */}
      <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full bg-emerald-500/5 dark:bg-emerald-500/10 blur-[120px] pointer-events-none select-none" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-emerald-500/3 dark:bg-emerald-500/5 blur-[100px] pointer-events-none select-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center flex-grow z-10 my-auto">
        {/* Left Column: Introductions & Giant Typography */}
        <div className="lg:col-span-8 flex flex-col justify-center h-full">


          <div className="relative">
            {/* Huge display text */}
            <motion.h1
              initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 35 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-sans font-medium tracking-tight leading-[1.05] mb-5 text-neutral-900 dark:text-white hover:text-emerald-500 transition-colors duration-300 cursor-default"
            >
              Visual <span className="font-serif italic font-semibold text-neutral-500 dark:text-neutral-400 hover:text-emerald-400 block transition-colors duration-300">deezinar.</span>
            </motion.h1>

            {/* Slogan details */}
            <motion.p
              initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="text-sm sm:text-base text-neutral-500 dark:text-neutral-400 font-sans max-w-md leading-relaxed font-light mb-8"
            >
              Hi, I am Jahangir. I design robust digital layouts, visual identities, and interactive spaces with pixel-level precision, leveraging clean typography and kinetic momentum.
            </motion.p>
          </div>

          {/* Interactive magnetic action button */}
          <motion.div
            initial={isMobile ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-5"
          >
            <button
              onClick={handleScrollToWork}
              className="group flex items-center justify-between gap-6 border-2 border-emerald-500 bg-emerald-500 hover:bg-transparent dark:hover:bg-transparent text-white hover:text-emerald-500 transition-all duration-300 rounded-full px-8 py-4 font-mono text-xs font-bold tracking-widest uppercase overflow-hidden relative shadow-lg shadow-emerald-500/10"
              data-cursor="hover"
            >
              Explore Portfolio
              <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>

            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="group flex items-center justify-center gap-3 border border-neutral-300 dark:border-neutral-800 bg-neutral-100/50 dark:bg-neutral-900/50 hover:bg-neutral-200 dark:hover:bg-neutral-800/80 transition-colors duration-300 rounded-full px-8 py-4 font-mono text-xs font-bold tracking-widest text-neutral-800 dark:text-neutral-200 uppercase"
              data-cursor="hover"
            >
              Connect Now
            </a>
          </motion.div>
        </div>

        {/* Right Column: Interactive Graphic Artifacts */}
        <motion.div 
          className="lg:col-span-4 flex flex-col items-center lg:items-end justify-center h-full relative"
          animate={isMobile ? {} : {
            y: [0, -8, 0],
          }}
          transition={{
            y: {
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
            }
          }}
        >
          {/* Social buttons above the box */}
          <motion.div
            initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-12 flex gap-2 w-full sm:w-72 justify-center text-neutral-500 dark:text-neutral-400"
          >
            <a
              href="https://www.linkedin.com/in/jahangir420/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-neutral-200 dark:border-neutral-800 hover:border-emerald-500 hover:bg-emerald-500/5 transition-all text-neutral-600 dark:text-neutral-400 hover:text-emerald-500 rounded-full font-mono text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-white/40 dark:bg-transparent backdrop-blur-sm shadow-sm"
              data-cursor="hover"
            >
              <Linkedin className="w-3 h-3 text-neutral-500 dark:text-neutral-400" />
              LinkedIn
            </a>
            <a
              href="https://instagram.com/jahangir_khn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 border border-neutral-200 dark:border-neutral-800 hover:border-emerald-500 hover:bg-emerald-500/5 transition-all text-neutral-600 dark:text-neutral-400 hover:text-emerald-500 rounded-full font-mono text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-white/40 dark:bg-transparent backdrop-blur-sm shadow-sm"
              data-cursor="hover"
            >
              <Instagram className="w-3 h-3 text-neutral-500 dark:text-neutral-400" />
              Instagram
            </a>
          </motion.div>

          {/* Quick status details with elegant cards */}
          <motion.div
            initial={isMobile ? { opacity: 1, x: 0 } : { opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-4 w-full sm:w-72"
          >
            <motion.div
              animate={isMobile ? {} : {
                y: [0, 8, 0],
              }}
              transition={{
                y: {
                  duration: 5.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }
              }}
              className="flex flex-col gap-4 w-full border border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/30 backdrop-blur-sm p-6 rounded-2xl"
            >
              <div className="flex gap-4 items-start pb-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex items-center justify-center p-1.5 rounded-lg bg-emerald-500 text-white dark:bg-transparent dark:text-emerald-500 dark:p-0 dark:mt-1 mt-0.5 shrink-0">
                  <Globe className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Current Location</p>
                  <p className="font-sans text-xs font-semibold text-neutral-800 dark:text-neutral-200">Lahore, Pakistan / Global Remote</p>
                </div>
              </div>

              <div className="flex gap-4 items-start pb-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex h-4 w-4 relative items-center justify-center mt-1 shrink-0">
                  <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                </div>
                <div>
                  <p className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Availability</p>
                  <p className="font-sans text-xs font-semibold text-neutral-800 dark:text-neutral-200">Available For Contracts Worldwide</p>
                </div>
              </div>

              <div className="flex gap-4 items-start pb-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="flex items-center justify-center p-1.5 rounded-lg bg-emerald-500 text-white dark:bg-transparent dark:text-emerald-500 dark:p-0 dark:mt-1 mt-0.5 shrink-0">
                  <Award className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Focus Fields</p>
                  <p className="font-sans text-xs font-semibold text-neutral-800 dark:text-neutral-200">Consultancy, brand Re/Building & Marketing Campaigns</p>
                </div>
              </div>

              <div className="flex gap-4 items-start">
                <div className="flex items-center justify-center p-1.5 rounded-lg bg-emerald-500 text-white dark:bg-transparent dark:text-emerald-500 dark:p-0 dark:mt-1 mt-0.5 shrink-0">
                  <Users className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-mono text-[9px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Trusted By</p>
                  <p className="font-sans text-xs font-semibold text-neutral-800 dark:text-neutral-200">Below is the list of satisfied clients</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Ribbon Ticker Area */}
      <div className="w-full relative py-6 border-y border-neutral-200/50 dark:border-neutral-800/20 bg-neutral-50/5 dark:bg-neutral-950/5 mt-12 overflow-visible select-none z-20">
        <div className="w-full overflow-visible flex whitespace-nowrap">
          <div className="animate-marquee flex gap-24 sm:gap-36 items-center text-neutral-400 dark:text-neutral-600 overflow-visible">
            {/* Group 1 */}
            {clientLogos.map((client) => (
              <img 
                key={`g1-${client.id}`}
                src={getImageUrl(client.src1)} 
                alt={client.name} 
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain filter grayscale opacity-70 contrast-125 transition-all duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-[3] cursor-pointer relative z-10 hover:z-50"
                referrerPolicy="no-referrer"
              />
            ))}
            {/* Group 2 for seamless loop repetition */}
            {clientLogos.map((client) => (
              <img 
                key={`g2-${client.id}`}
                src={getImageUrl(client.src2)} 
                alt={client.name} 
                className="w-12 h-12 sm:w-16 sm:h-16 object-contain filter grayscale opacity-70 contrast-125 transition-all duration-300 hover:opacity-100 hover:grayscale-0 hover:scale-[3] cursor-pointer relative z-10 hover:z-50"
                referrerPolicy="no-referrer"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
